import { keyBy } from 'lodash';
import { useMemo } from 'react';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useListService from '@/data/hook/useListService';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import CoursePath from '@/models/CoursePath';
import Evaluation from '@/models/Evaluation';

export type MasterSlaveEntry = {
  slave: Evaluation;
  coursePath: CoursePath;
};

type UseMasterSlaveMapProps = {
  courseLineId: number;
  masters: Evaluation[];
  slaves: Evaluation[];
};

export const useMasterSlaveMap = ({
  courseLineId,
  masters,
  slaves,
}: UseMasterSlaveMapProps) => {
  const enabled = slaves.length > 0;
  const { results: coursePaths, isInitialLoading: isQueryLoading } =
    useListService({
      ...coursePathsQueryKeys.list({
        courseLineId: [courseLineId],
        pageSize: ORDENABLE_PAGE_SIZE,
      }),
      enabled,
      staleTime: REQUEST_STALE_TIME_IN_MS,
    });
  const isLoading = enabled && isQueryLoading;

  const slavesByMaster = useMemo(() => {
    const coursePathById = keyBy(coursePaths, 'id');
    const map = new Map<number, MasterSlaveEntry[]>(
      masters.map(evaluation => [evaluation.id, []]),
    );

    slaves.forEach(slave => {
      const { sourceTemplateId, coursePathId } = slave;
      if (sourceTemplateId == null || coursePathId == null) return;
      const coursePath = coursePathById[coursePathId];
      const entries = map.get(sourceTemplateId);
      if (coursePath && entries) entries.push({ slave, coursePath });
    });
    return map;
  }, [masters, slaves, coursePaths]);

  return {
    slavesByMaster,
    isLoading,
  };
};
